import { useState, useCallback, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useAccessToken } from '../../../fetchData/useAccessToken'
import { makeResultFetch } from '../../../fetchData/genericFetch'
import { apiRequestParams } from '../../../fetchData/fetchOptions'
import {
  ProjectForecastReportRow,
  ProjectForecastReportPeriod,
} from '../../../interfaces/systemSettings/ProjectForecastReport'

// Helper function to add delay between requests
const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms))

// Helper function to retry failed requests
const retryWithDelay = async <T,>(
  fn: () => Promise<T>,
  maxRetries: number = 2,
  delayMs: number = 500
): Promise<T> => {
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn()
    } catch (error) {
      if (attempt === maxRetries) {
        throw error
      }
      await delay(delayMs * (attempt + 1)) // Exponential backoff
    }
  }
  throw new Error('Max retries exceeded')
}

interface BulkCompleteState {
  isProcessing: boolean
  currentIndex: number
  totalCount: number
  currentProjectName: string
  successCount: number
  failures: Array<{ projectName: string; error: string }>
  isComplete: boolean
}

interface UseBulkCompleteProjectForecastReturn {
  state: BulkCompleteState
  startBulkComplete: (
    reports: ProjectForecastReportRow[],
    year: number,
    period: ProjectForecastReportPeriod
  ) => Promise<void>
  cancel: () => void
  reset: () => void
}

const initialState: BulkCompleteState = {
  isProcessing: false,
  currentIndex: 0,
  totalCount: 0,
  currentProjectName: '',
  successCount: 0,
  failures: [],
  isComplete: false,
}

export const useBulkCompleteProjectForecast =
  (): UseBulkCompleteProjectForecastReturn => {
    const [state, setState] = useState<BulkCompleteState>(initialState)
    const getAccessToken = useAccessToken()
    const queryClient = useQueryClient()
    const cancelledRef = useRef(false)

    const reset = useCallback(() => {
      setState(initialState)
      cancelledRef.current = false
    }, [])

    const cancel = useCallback(() => {
      cancelledRef.current = true
    }, [])

    const completeProjectForecast = useCallback(
      async (projectId: string, reportGuid: string): Promise<void> => {
        // Get a fresh access token for each request
        const accessToken = await getAccessToken()

        if (!accessToken) {
          throw new Error('Access token är undefined')
        }

        console.log(
          'Making API call to:',
          `/reports/${projectId}/projectForecast/${reportGuid}/complete`
        )

        const response = await makeResultFetch<boolean>(
          `/reports/${projectId}/projectForecast/${reportGuid}/complete`,
          apiRequestParams(accessToken, 'POST')
        ).catch((error) => {
          console.error('API call failed:', error)
          throw new Error(
            'Det gick inte att ansluta till servern. Kontrollera din internetuppkoppling.'
          )
        })

        if (!response.success) {
          throw new Error(response.message)
        }
      },
      [getAccessToken]
    )

    const startBulkComplete = useCallback(
      async (
        reports: ProjectForecastReportRow[],
        year: number,
        period: ProjectForecastReportPeriod
      ) => {
        cancelledRef.current = false

        setState({
          isProcessing: true,
          currentIndex: 0,
          totalCount: reports.length,
          currentProjectName: reports[0]?.projectName ?? '',
          successCount: 0,
          failures: [],
          isComplete: false,
        })

        const failures: Array<{ projectName: string; error: string }> = []
        let successCount = 0

        for (let i = 0; i < reports.length; i++) {
          if (cancelledRef.current) {
            setState((prev) => ({
              ...prev,
              isProcessing: false,
              isComplete: true,
            }))
            return
          }

          const report = reports[i]

          setState((prev) => ({
            ...prev,
            currentIndex: i,
            currentProjectName: report.projectName,
          }))

          try {
            await retryWithDelay(() =>
              completeProjectForecast(report.projectId, report.id)
            )
            successCount++
          } catch (error) {
            const errorMessage =
              error instanceof Error ? error.message : 'Okänt fel uppstod'
            failures.push({
              projectName: report.projectName,
              error: errorMessage,
            })
          }
        }

        // Invalidate cache after all completions (or cancellation)
        await queryClient.invalidateQueries({
          queryKey: ['projectForecastReports', year, period],
        })

        setState({
          isProcessing: false,
          currentIndex: reports.length - 1,
          totalCount: reports.length,
          currentProjectName: '',
          successCount,
          failures,
          isComplete: true,
        })
      },
      [completeProjectForecast, queryClient]
    )

    return {
      state,
      startBulkComplete,
      cancel,
      reset,
    }
  }
